import { FormControl, FormLabel } from '@chakra-ui/form-control';
import { Box, PopoverBody, PopoverFooter, VStack } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'urql';

import { useDebounce } from '@revelio/core';

import { Autocomplete } from '../../../../../../shared/components/Autocomplete';
import {
  buildTxtNode,
  wrapExprNode,
} from '../../../../components/DataProvider/talentDiscovery/node-builders';
import { getRoleTaxonomyId } from '../../../../components/DataProvider/talentDiscovery/roles';
import { getSkillTaxonomyId } from '../../../../components/DataProvider/talentDiscovery/skills';
import { TalentDiscoveryV3Tags } from '../../../../components/DataProvider/talentDiscovery/types';
import { useFilterState, useFilters } from '../../../hooks/useFilters';
import { FilterType, type FreeTextFilter } from '../../../types/filters';
import { FilterSelectFooter } from '../../FilterSelectFooter';
import { GET_NAME_RECS, isValidSearchString } from './helpers';

interface NameFilterFormProps {
  filter: FreeTextFilter;
  closeMenu: () => void;
}

export const NameFilterForm = ({ filter, closeMenu }: NameFilterFormProps) => {
  const { filterState, updateFilter } = useFilterState(filter.id);
  const { filters } = useFilters();
  const [name, setName] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchString = useDebounce(name, 400);
  const [recommendations, setRecommendations] = useState<
    { value: string; label: string }[]
  >([]);

  const nameRecommendationVariables = useMemo(() => {
    if (!isValidSearchString(debouncedSearchString)) return undefined;
    const trimmedName = debouncedSearchString.trim();
    if (!trimmedName) return undefined;

    const expr = wrapExprNode(
      buildTxtNode(TalentDiscoveryV3Tags.Name, trimmedName)
    );
    const activeFilters = Array.from(filters.values());

    return {
      query: {
        filters: {
          expr,
          roles_tax: getRoleTaxonomyId(activeFilters),
          skills_tax: getSkillTaxonomyId(activeFilters),
        },
        pagination: { page_num: 1, page_size: 100 },
      },
    };
  }, [debouncedSearchString, filters]);

  const [{ data, fetching }] = useQuery({
    query: GET_NAME_RECS,
    variables: nameRecommendationVariables,
    pause: !nameRecommendationVariables,
  });

  useEffect(() => {
    if (data?.talent_discovery_name_recommendations_v3) {
      const newRecommendations =
        data.talent_discovery_name_recommendations_v3.names.map((rec) => ({
          value: rec.name,
          label: rec.name,
        }));
      setRecommendations(newRecommendations || []);
    } else {
      setRecommendations([]);
    }
  }, [data]);

  useEffect(() => {
    if (isValidSearchString(name)) return;
    setRecommendations([]);
  }, [name]);

  useEffect(() => {
    // Small delay to ensure the input is mounted and Popover is fully open
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (filterState.type === FilterType.FREE_TEXT) {
      const existingName = filterState.text?.[0] || '';
      setName(existingName);
    }
  }, [filterState]);

  const handleSubmit = () => {
    if (filterState.type === FilterType.FREE_TEXT) {
      updateFilter({
        ...filterState,
        text: name ? [name] : [],
        isUserSubmitted: true,
      });
    }
    closeMenu();
  };

  const handleClear = () => {
    setName('');
  };

  const isAddDisabled = !isValidSearchString(name);

  return (
    <VStack spacing={4} align="stretch">
      <PopoverBody padding={0}>
        <Box onClick={(e) => e.stopPropagation()} mt={1}>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Name</FormLabel>
            <Autocomplete
              inputId="custom-dash-name-search"
              placeholder="Search for a name"
              inputValue={name}
              setInputValue={setName}
              suggestions={recommendations}
              dropdownHeader="Suggestions"
              showLoader
              isLoading={fetching}
              isTypeaheadDisabled={false}
              initialFocusRef={inputRef}
            />
          </FormControl>
        </Box>
      </PopoverBody>
      <PopoverFooter padding={0} borderTopWidth={0}>
        <FilterSelectFooter
          onClearSelections={handleClear}
          onClose={closeMenu}
          onAdd={handleSubmit}
          addLabel="Add"
          isAddDisabled={isAddDisabled}
        />
      </PopoverFooter>
    </VStack>
  );
};
